<template>
  <PermissionGate role="admin">
    <template v-slot:unauthorised>
      <VMessage
        type="warning"
        :fullwidth="true"
        class="p-partials-users__message-margin"
      >
        <template v-slot:icon>
          <ExclamationTriangleIcon />
        </template>
        <template v-slot:text>
          {{ $t('teams.permissionWarning') }}
        </template>
      </VMessage>
    </template>
    <div v-if="!canAccess">
      <TeamsUpsell
        class="mb-8"
        :canUpgrade="canUpgrade"
        :hasTried="hasTried"
      />
      <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-base blur-sm pointer-events-none select-none">
        <GroupCard
          v-for="(group, index) in results"
          :key="index"
          :name="group.name"
          :loading="loading"
          :created_at="formattedCreatedDate(group.created_at)"
          :created_by="group.created_by"
          :members="group.users_count"
        />
      </div>
    </div>
    <div v-else>
      <div class="md:flex md:justify-between mb-base">
        <div
          v-if="isFreeTrialPlan"
          class="mb-base md:mb-0"
        >
          <i18n path="upsells.teams.freeTrialSetTeamsExplainer">
            <template v-slot:link>
              <a
                  href="https://www.signable.co.uk/plans/"
                  class="text-primary"
                  data-ec="plu-upsell"
                  data-et="free-trial-set-teams-explainer"
                  target="_blank"
                >
                  <br> {{ $t('upsells.learnMoreMessage') }}
                </a>
            </template>
          </i18n>
        </div>
        <button
          @click="openAddGroupModal"
          data-ec="create_team_page"
          data-et="create_new_group_button"
          class="tw-button tw-button-cta"
        >
          <PlusIcon />
          <span>{{ $t("groups.createTeam") }}</span>
        </button>
      </div>
      <div v-if="!results.length">
        <h3>{{ $t("groups.noTeams") }}</h3>
      </div>
      <div v-else>
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-base">
          <GroupCard
            v-for="(group, index) in results"
            :key="index"
            @activated="openEditGroupModal(group.id)"
            :name="group.name"
            :loading="loading"
            :created_at="formattedCreatedDate(group.created_at)"
            :created_by="group.created_by"
            :members="group.users_count"
          />
        </div>
        <div class="mt-base">
          <VPaginate
            class="mb-base"
            v-if="resultsMeta.total > resultsMeta.per_page"
            storeAction="groups/PAGINATE"
            :previous="$t('functional.previous')"
            :next="$t('functional.next')"
            :current="resultsMeta.current_page"
            :last="resultsMeta.last_page"
            :showUrl="false"
          />
          <PageResultsCounter
            v-if="!loading"
            :from="resultsMeta.from"
            :to="resultsMeta.to"
            :total="resultsMeta.total"
            :type="$t('functional.teams')"
          />
        </div>
      </div>
      <!-- Modals -->
      <CreateGroupModal
        :show="showCreateGroupModal"
        @close="showCreateGroupModal = false"
      />
    </div>
    <EditGroupModal
      :groupId="groupId"
      :show="showEditGroupModal"
      @groupUpdated="loadData"
      @close="showEditGroupModal = false"
    />
  </PermissionGate>
</template>

<script>
import GroupCard from '@/components/tailwind/GroupCard.vue';
import EditGroupModal from '@/views/modals/group/EditGroupModal.vue';
import VPaginate from '@/components/deprecated/molecules/Paginate.vue';
import VMessage from '@/components/deprecated/atoms/Message.vue';
import localeDateFormatter from '@/utils/locale-date-formatter';
import PageResultsCounter from '@/views/components/PageResultsCounter.vue';
import { mapGetters } from 'vuex';
import CreateGroupModal from '@/views/modals/group/CreateGroupModal.vue';
import {
  ExclamationTriangleIcon,
  PlusIcon,
} from '@/components/icons/fontIcons';
import { useTeamsUpsell } from '@/composables/teams';
import { TeamsUpsell } from '@/components/ui/teams/upsells/TeamsUpsell';
import { PermissionGate } from '@/components/gates/PermissionGate';

export default {
  name: 'GroupsPartial',
  components: {
    GroupCard,
    EditGroupModal,
    PageResultsCounter,
    VMessage,
    VPaginate,
    CreateGroupModal,
    ExclamationTriangleIcon,
    PlusIcon,
    TeamsUpsell,
    PermissionGate,
  },
  setup() {
    const { canUpgrade, canAccess, hasTried } = useTeamsUpsell();

    return {
      canUpgrade,
      canAccess,
      hasTried,
    };
  },
  data() {
    return {
      loading: false,
      pageLimit: 45,
      showCreateGroupModal: false,
      showEditGroupModal: false,
      editGroupModalLoading: false,
      group: {},
      groupId: null,
    };
  },
  mounted() {
    this.loadData(1);
  },
  watch: {
    hasLoaded(value) {
      this.loading = !value;
    },
  },
  computed: {
    ...mapGetters({
      groups: 'groups/pagedResults',
      totalPages: 'groups/totalPages',
      totalCount: 'groups/totalCount',
      hasLoaded: 'groups/hasLoaded',
      resultsMeta: 'groups/meta',
      userHasRole: 'roles/userHasRole',
      plan: 'plans/current',
    }),
    results() {
      if (this.loading) {
        return new Array(3).fill({
          name: '',
          members: 1,
          created_by: '',
          created_at: '',
        });
      }
      return this.groups;
    },
    isFreeTrialPlan() {
      return this.plan.id === 'free-trial';
    },
  },
  methods: {
    async loadData(page) {
      this.loading = true;
      await this.$store.dispatch('groups/PAGINATE', { page, pageLimit: 45 });
      this.loading = false;
    },
    openAddGroupModal() {
      this.showCreateGroupModal = true;
    },
    formattedCreatedDate(date) {
      return localeDateFormatter('calendar', date);
    },
    openEditGroupModal(groupId) {
      this.groupId = groupId;
      this.editGroupModalLoading = true;
      this.showEditGroupModal = true;
    },
  },
};
</script>
